import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { FieldInfo } from '@root/Services/QueryExpr';
import { CurrencyPreferenceService } from '@root/Services/Customers/CurrencyPreferenceService';
import { inject, singleton } from 'tsyringe';
import { VisibleSpaces } from '../Text/VisibleSpaces';
import { AnonymousValueGridCell } from './Components';
import { DataColumnConfig, AccessorPath, AnonymousValue, ColumnConfig } from './Models';

export interface FieldInfoColumnModifier {
    exclude?: boolean;
    group?: string;
    name?: string;
    format?: string;
    idAsPath?: boolean;
    helpText?: string;
}

@singleton()
export class FieldInfoColumnAdapter<T extends object> {
    public constructor(
        @inject(FormatService) private readonly formatterSvc: FormatService,
        @inject(CurrencyPreferenceService) private readonly currencyPrefs: CurrencyPreferenceService
    ) {}

    public adapt(fieldInfo: FieldInfo, modifiers?: FieldInfoColumnModifier) {
        const filter = this.getFilterType(fieldInfo);
        const fmtSvc = this.formatterSvc;
        return {
            id: modifiers?.idAsPath ? fieldInfo.path : fieldInfo.pathWithRoot,
            accessor: this.createAccessor(this.getAccessorPath(fieldInfo)),
            defaultWidth: this.getDefaultWidth(fieldInfo),
            header: fieldInfo.name,
            headerRenderer: this.createHeaderRenderer(fieldInfo),
            sortField: fieldInfo.path,
            formatter: this.getFormatter(modifiers?.format ?? fieldInfo.format ?? fieldInfo.typeName, fieldInfo),
            helpText: modifiers?.helpText ?? fieldInfo.helpText,
            groupName: modifiers?.group ?? fieldInfo.groupName,
            cellRenderer(item: T) {
                const formatter =
                    typeof this.formatter !== 'string' ? this.formatter : (_: T, value: any) => fmtSvc.format(this.formatter as NamedFormats, value);
                return <AnonymousValueGridCell formatter={formatter} value={(this.accessor as (item: T) => AnonymousValue)(item)} />;
            },
            type: fieldInfo.typeName,
            filter: filter
                ? {
                      filterType: filter,
                      name: fieldInfo.name,
                      filterField: fieldInfo.path,
                  }
                : undefined,
            align: this.getDefaultAlignment(fieldInfo),
            minWidth: 30,
        } as DataColumnConfig<T>;
    }

    public createAccessor(accessorPath: AccessorPath) {
        const valueCache = new WeakMap<T, AnonymousValue>();
        const unwrapValue = (item: T) => {
            let value = valueCache.get(item);
            if (!value) {
                valueCache.set(item, (value = this.evaluatePath(item, accessorPath)));
            }
            return value;
        };

        return (item: T) => {
            return unwrapValue(item);
        };
    }

    public getAccessorPath(field: FieldInfo): AccessorPath {
        const accessorPath = field
            .getPath()
            .slice(0, -1)
            .map((f) => ({ accessor: f, isLeaf: false }));
        accessorPath.push({ accessor: field.fieldName ?? '', isLeaf: true });
        return accessorPath;
    }

    private createHeaderRenderer(fieldInfo: FieldInfo) {
        let result: ((item: ColumnConfig<T>) => React.ReactNode) | undefined = undefined;

        if (fieldInfo.flags.has('tag')) {
            result = (c) => <VisibleSpaces value={c.header ?? fieldInfo.name} />;
        }

        return result;
    }

    private evaluatePath(item: T, path: { accessor: string; isLeaf: boolean }[]) {
        const result: AnonymousValue = { first: undefined, values: [], hasMany: false };
        let children: Record<string, any>[] = [item];

        for (const accessor of path) {
            if (accessor.isLeaf) {
                for (const child of children) {
                    const value = child[accessor.accessor];
                    if (value instanceof Array) {
                        result.hasMany = true;
                        result.values.push(...value);
                    } else {
                        result.values.push(value);
                    }
                }
            } else {
                const nextChildren: Record<string, any>[] = [];
                for (const child of children) {
                    const value = child[accessor.accessor];
                    if (value instanceof Array) {
                        result.hasMany = true;
                        nextChildren.push(...value);
                    } else if (typeof value === 'object' && value !== null) {
                        nextChildren.push(value);
                    }
                }
                children = nextChildren;
            }
        }

        result.first = result.values[0];

        return result;
    }

    private getDefaultWidth(field: FieldInfo) {
        switch (field.typeName) {
            case 'string':
                return 180;
            case 'number':
                return 120;
            case 'date':
                return 120;
            case 'boolean':
                return field.name.length < 6 ? 50 : 100;
            default:
                return 140;
        }
    }
    private getDefaultAlignment(field: FieldInfo) {
        switch (field.typeName) {
            case 'number':
                return 'right';
            case 'boolean':
                return 'center';
            default:
                return 'left';
        }
    }

    private getFilterType(field: FieldInfo) {
        switch (field.typeName) {
            case 'string':
                return 'string';
            case 'number':
                return 'number';
            case 'date':
                return 'date';
            case 'boolean':
                return 'boolean';
            default:
                return null;
        }
    }

    private getFormatter(formatterName?: string, fieldInfo?: FieldInfo) {
        switch (formatterName) {
            case 'whole-percent':
                return (_: T, value: number) =>
                    typeof value !== 'number' ? '' : value < 1 && value > 0 ? `< 1%` : `${this.formatterSvc.formatInt0Dec(value)}%`;
            default:
                // Removed debug logging for getFormatter
                const isMultiCurrencyField = fieldInfo!.flags.has('multi-currency');
                const isMoneyFormat = formatterName?.startsWith('money') || formatterName === 'currency-2dec' || formatterName === 'currency-4dec';

                if (isMultiCurrencyField && isMoneyFormat) {
                    // For multi-currency fields, use user's preferred currency
                    const userCurrency = this.currencyPrefs.getCurrency();

                    // Extract decimal places from format (e.g., 'money', 'money-0', 'money-4')
                    let decimals = 2; // default
                    if (formatterName) {
                        const parts = formatterName.split('-');
                        if (parts.length > 1) {
                            const parsedDecimals = parseInt(parts[1], 10);
                            if (!isNaN(parsedDecimals)) {
                                decimals = parsedDecimals;
                            }
                        }
                    }

                    // Return a formatter that uses the user's preferred currency
                    return (_: T, value: number) => {
                        if (typeof value !== 'number') return '';
                        return this.formatterSvc.formatMoneyWithCurrency(value, userCurrency, decimals);
                    };
                }

                // For non-multi-currency fields, use standard formatting
                const formatter = this.formatterSvc.getFormatter(formatterName ?? '');
                if (formatter) {
                    const result = (_: T, value: any) => formatter.format(value);
                    result.formatName = formatter.id;
                    return result;
                }
                return (_: T, value: string) => value;
        }
    }
}
